import type { SoftphoneConnectionState } from 'Modules/softphone/components/SoftphoneConnectionStatus/index.types';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useCallback } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';

export interface UseConnectionAnalyticsReturn {
  trackConnectionStatusChange: (
    fromStatus: SoftphoneConnectionState,
    toStatus: SoftphoneConnectionState
  ) => void;
  trackPoorConnectionTooltipShown: () => void;
  trackConnectionTooltipOpened: () => void;
  trackLostConnectionDetected: () => void;
  trackLostConnectionBannerShown: () => void;
  trackLostConnectionAudioStarted: () => void;
  trackConnectionRestored: (fromStatus: SoftphoneConnectionState) => void;
  trackCallEndedDuringConnectionIssue: (
    connectionStatus: SoftphoneConnectionState
  ) => void;
}

/**
 * Hook to track connection-related analytics events
 */
export function useConnectionAnalytics(): UseConnectionAnalyticsReturn {
  const hasActiveCall = useSoftphoneStore(
    (state) => state.activeCalls.size > 0
  );

  const trackConnectionStatusChange = useCallback(
    (
      fromStatus: SoftphoneConnectionState,
      toStatus: SoftphoneConnectionState
    ) => {
      pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.CONNECTION_STATUS_CHANGED, {
        from_status: fromStatus,
        to_status: toStatus,
        has_active_call: hasActiveCall,
      });
    },
    [hasActiveCall]
  );

  const trackPoorConnectionTooltipShown = useCallback(() => {
    pushToGTMDataLayer(
      ANALYTICS_EVENT_SOFTPHONE.POOR_CONNECTION_TOOLTIP_SHOWN,
      {
        has_active_call: hasActiveCall,
      }
    );
  }, [hasActiveCall]);

  const trackConnectionTooltipOpened = useCallback(() => {
    pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.CONNECTION_TOOLTIP_OPENED, {
      has_active_call: hasActiveCall,
    });
  }, [hasActiveCall]);

  const trackLostConnectionBannerShown = useCallback(() => {
    pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.LOST_CONNECTION_BANNER_SHOWN, {
      has_active_call: hasActiveCall,
    });
  }, [hasActiveCall]);

  const trackLostConnectionDetected = useCallback(() => {
    pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.LOST_CONNECTION_DETECTED, {
      has_active_call: hasActiveCall,
    });
  }, [hasActiveCall]);

  const trackLostConnectionAudioStarted = useCallback(() => {
    pushToGTMDataLayer(
      ANALYTICS_EVENT_SOFTPHONE.LOST_CONNECTION_AUDIO_STARTED,
      {
        has_active_call: hasActiveCall,
      }
    );
  }, [hasActiveCall]);

  const trackConnectionRestored = useCallback(
    (fromStatus: SoftphoneConnectionState) => {
      pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.CONNECTION_RESTORED, {
        from_status: fromStatus,
        has_active_call: hasActiveCall,
      });
    },
    [hasActiveCall]
  );

  const trackCallEndedDuringConnectionIssue = useCallback(
    (connectionStatus: SoftphoneConnectionState) => {
      pushToGTMDataLayer(
        ANALYTICS_EVENT_SOFTPHONE.CALL_ENDED_DURING_CONNECTION_ISSUE,
        {
          connection_status: connectionStatus,
        }
      );
    },
    []
  );

  return {
    trackConnectionStatusChange,
    trackPoorConnectionTooltipShown,
    trackConnectionTooltipOpened,
    trackLostConnectionDetected,
    trackLostConnectionBannerShown,
    trackLostConnectionAudioStarted,
    trackConnectionRestored,
    trackCallEndedDuringConnectionIssue,
  };
}
